import { useEffect, useRef, useState } from 'react';
import { type BopWsMessage, useBopWebSocket, type WsTrade } from '@/lib/websocket';

/** Cap on retained live rows — legacy grew the buffer unboundedly. */
const DEFAULT_MAX_BUFFER = 100;
/** How long a row stays flagged for flash styling (legacy setTimeout(2000)). */
const DEFAULT_FLASH_DURATION_MS = 2_000;

const EMPTY_FLASH_IDS: ReadonlySet<string> = new Set();

export type TradeFeedMessageType = 'trade' | 'instantTrade';

export interface UseTradeFeedOptions {
  /**
   * Drop messages whose data.accountHolderId !== this value (user-console usage). NOTE:
   * live trades by SUBACCOUNTS are dropped while REST includes them —
   * legacy parity; do not "fix" without product sign-off.
   */
  accountHolderId?: string;
  /**
   * Which WS message types to ingest. Undefined (the default) ingests both `trade` and
   * `instantTrade` — TradingPanel's usage, which sees every trade. The
   * user-console Trades tab passes a one-element array per sub-tab: pass a
   * module-level constant so effect deps stay stable across renders.
   */
  messageTypes?: ReadonlyArray<TradeFeedMessageType>;
  /** Cap on retained live rows, newest first. Default 100. */
  maxBuffer?: number;
  /** How long a row stays flagged for flash styling. Default 2000 ms. */
  flashDurationMs?: number;
}

export interface UseTradeFeedResult {
  /** Newest-first live trades received this session (post-filter, capped). */
  trades: WsTrade[];
  /** tradeIds currently inside their flash window. */
  flashTradeIds: ReadonlySet<string>;
}

/**
 * Shared live trade feed — trading page + user console. Built on the
 * app-wide shared socket (`share: true`), so mounting this hook never opens a
 * second connection. The buffer persists across reconnects and only resets on
 * unmount; there is no replay for trades missed while disconnected
 * (the REST pages are the source of truth).
 */
export const useTradeFeed = ({
  accountHolderId,
  messageTypes,
  maxBuffer = DEFAULT_MAX_BUFFER,
  flashDurationMs = DEFAULT_FLASH_DURATION_MS,
}: UseTradeFeedOptions = {}): UseTradeFeedResult => {
  const { lastJsonMessage } = useBopWebSocket();
  const [trades, setTrades] = useState<WsTrade[]>([]);
  const [flashTradeIds, setFlashTradeIds] = useState<ReadonlySet<string>>(EMPTY_FLASH_IDS);
  const processedMessageRef = useRef<BopWsMessage | null>(null);
  const flashTimeoutsRef = useRef(new Set<ReturnType<typeof setTimeout>>());

  // The ingestion filter only applies to future messages — prune the existing
  // buffer when the account filter changes mid-mount (review follow-up for the
  // user-console consumer, which can switch accounts without remounting).
  const previousAccountRef = useRef(accountHolderId);
  useEffect(() => {
    if (previousAccountRef.current === accountHolderId) return;
    previousAccountRef.current = accountHolderId;
    if (accountHolderId) setTrades((previous) => previous.filter((trade) => trade.accountHolderId === accountHolderId));
  }, [accountHolderId]);

  useEffect(() => {
    // react-use-websocket returns a NEW object per message — the identity guard
    // processes each message exactly once even when the effect re-runs
    if (!lastJsonMessage || processedMessageRef.current === lastJsonMessage) return;
    processedMessageRef.current = lastJsonMessage;

    // The backend only emits `instantTrade` today; the legacy `trade` variant is
    // tolerated identically in case that stream ever revives. Everything
    // else (incl. fileDownload) is ignored.
    if (lastJsonMessage.type !== 'instantTrade' && lastJsonMessage.type !== 'trade') return;
    // The per-sub-tab split: when messageTypes is set, each mount ingests
    // only its own message types. Undefined ingests both (TradingPanel's usage).
    if (messageTypes && !messageTypes.includes(lastJsonMessage.type)) return;
    const trade = lastJsonMessage.data;
    if (accountHolderId && trade.accountHolderId !== accountHolderId) return;

    setTrades((previous) => [trade, ...previous].slice(0, maxBuffer));
    setFlashTradeIds((previous) => new Set(previous).add(trade.tradeId));

    const handle = setTimeout(() => {
      flashTimeoutsRef.current.delete(handle);
      setFlashTradeIds((previous) => {
        if (!previous.has(trade.tradeId)) return previous;
        const next = new Set(previous);
        next.delete(trade.tradeId);
        return next;
      });
    }, flashDurationMs);
    // Buffer eviction does not cancel the flash timeout — harmless
    flashTimeoutsRef.current.add(handle);
  }, [lastJsonMessage, accountHolderId, messageTypes, maxBuffer, flashDurationMs]);

  // All pending flash timers die with the hook
  useEffect(() => {
    const timeouts = flashTimeoutsRef.current;
    return () => {
      for (const handle of timeouts) clearTimeout(handle);
    };
  }, []);

  return { trades, flashTradeIds };
};
